import { BoxProps } from '@material-ui/core';

import omit from 'lodash/omit';
import pick from 'lodash/pick';

import { CodeEditor, LeftBorderBox } from '@spotify/confidence-core-react';

import {
  AVERAGE_KEYS,
  AverageCodeSnippetProps,
  MeasurementInput,
  RATIO_KEYS,
  RatioCodeSnippetProps,
  averageCodeSnippet,
  averageMeasurementSnippet,
  ratioCodeSnippet,
  ratioMeasurementSnippet,
  toAverageSnippetConfig,
  toRatioSnippetConfig,
} from './snippets';

export {
  AVERAGE_KEYS,
  RATIO_KEYS,
  averageCodeSnippet,
  averageMeasurementSnippet,
  ratioCodeSnippet,
  ratioMeasurementSnippet,
  toAverageSnippetConfig,
  toRatioSnippetConfig,
} from './snippets';
export type {
  AverageCodeSnippetProps,
  MeasurementInput,
  RatioCodeSnippetProps,
} from './snippets';

interface SharedSimplifiedQueryProps extends BoxProps {
  query?: string;
}

const SimplifiedQuery = ({
  query,
  ...boxProps
}: SharedSimplifiedQueryProps) => (
  <LeftBorderBox {...boxProps}>
    <CodeEditor value={query} withBorder={false} readOnly />
  </LeftBorderBox>
);

const renderQuery =
  <Props extends object>(
    keys: ReadonlyArray<keyof Props>,
    snippet: (cfg: Props) => string,
  ) =>
  (props: Props & Omit<SharedSimplifiedQueryProps, 'query'>) => (
    <SimplifiedQuery
      {...omit(props, keys as readonly string[])}
      query={snippet(pick(props, keys as readonly string[]) as Props)}
    />
  );

export const AverageSimplifiedQuery = renderQuery<AverageCodeSnippetProps>(
  AVERAGE_KEYS,
  averageCodeSnippet,
);

export const AverageMeasurementQuery = renderQuery<AverageCodeSnippetProps>(
  AVERAGE_KEYS,
  averageMeasurementSnippet,
);

export const RatioSimplifiedQuery = renderQuery<RatioCodeSnippetProps>(
  RATIO_KEYS,
  ratioCodeSnippet,
);

export const RatioMeasurementQuery = renderQuery<RatioCodeSnippetProps>(
  RATIO_KEYS,
  ratioMeasurementSnippet,
);

type MetricSimplifiedQueryProps = Omit<SharedSimplifiedQueryProps, 'query'> & {
  measurement: MeasurementInput;
};

export const MetricSimplifiedQuery = ({
  measurement,
  ...props
}: MetricSimplifiedQueryProps) => {
  const averageConfig = toAverageSnippetConfig(measurement);
  if (averageConfig) {
    return <AverageSimplifiedQuery {...props} {...averageConfig} />;
  }

  const ratioConfig = toRatioSnippetConfig(measurement);
  if (ratioConfig) {
    return <RatioSimplifiedQuery {...props} {...ratioConfig} />;
  }

  return null;
};
